import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, FileText, AlertCircle, CheckCircle2, ScanLine, Replace, Printer, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import DamageDispositionDialog from '@/components/DamageDispositionDialog';

export default function DamageInventoryReport() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await apiClient.get(`/damage/inventory?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch {
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pendingCount = items.filter(it => !it.received_at).length;
  const undispositionedCount = items.filter(it => it.received_at && !it.result).length;

  // ---- Disposition + Reprint ----
  const [disposeRow, setDisposeRow] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-base font-semibold">Damage Inventory Report</h1>
            <p className="text-xs text-gray-400">{total} total records</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-[#F9FAFB] hover:bg-white/10"
            onClick={() => navigate('/wms/fg/usn-label-reprint', { state: { fromDamage: true } })}
            data-testid="open-reprint-page-btn"
          >
            <Printer className="w-4 h-4 mr-1" />
            Reprint Sticker
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 space-y-4">
        {/* Approval banner — appears when ANY rows are still PENDING */}
        {pendingCount > 0 && (
          <Card className="border-amber-200 bg-amber-50/70" data-testid="approval-banner">
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {pendingCount} pending record{pendingCount === 1 ? '' : 's'} need physical receipt
                </p>
                <p className="text-xs text-amber-800/80 mt-0.5">
                  Damage records flip to <b>Received</b> only after the physical
                  USN is scanned at the Damage Section. Open the receipt screen,
                  scan each USN, and the status will update automatically.
                </p>
              </div>
              <Button
                onClick={() => navigate('/wms/damage/receipt')}
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                data-testid="open-damage-receipt-btn"
              >
                <ScanLine className="w-4 h-4 mr-1" />
                Open Damage Section Receipt
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2" data-testid="filters">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search USN, bundle, SKU, design..."
                className="pl-9"
                data-testid="search-input"
              />
            </div>
          </form>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-gray-200 px-3 text-sm bg-white"
            data-testid="status-filter"
          >
            <option value="">All Status</option>
            <option value="DAMAGED">Damaged (Pending Receipt)</option>
            <option value="RECEIVED">Received</option>
          </select>
        </div>

        {/* Action banner — RECEIVED rows still awaiting Cleared/Damaged decision */}
        {undispositionedCount > 0 && (
          <Card className="border-blue-200 bg-blue-50/70" data-testid="dispose-banner">
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <ScanLine className="w-5 h-5 text-blue-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900">
                  {undispositionedCount} received USN{undispositionedCount === 1 ? '' : 's'} awaiting disposition
                </p>
                <p className="text-xs text-blue-800/80 mt-0.5">
                  Re-check each piece and click <b>Disposition</b> on the row to mark it
                  Cleared (replace USN, keep original SKU) or Damaged (convert to a No
                  Exchange item). Use <b>Reprint Label</b> to issue the new sticker.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="damage-table">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-gray-600">USN</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Bundle</th>
                  <th className="px-3 py-2 font-medium text-gray-600">SKU</th>
                  <th className="px-3 py-2 font-medium text-gray-600 hidden md:table-cell">Design</th>
                  <th className="px-3 py-2 font-medium text-gray-600 hidden lg:table-cell">Shade</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Remarks</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Source</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Status</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Disposition</th>
                  <th className="px-3 py-2 font-medium text-gray-600 hidden md:table-cell">Marked By</th>
                  <th className="px-3 py-2 font-medium text-gray-600 hidden lg:table-cell">Marked At</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="11" className="px-3 py-8 text-center text-gray-400">Loading...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="11" className="px-3 py-8 text-center text-gray-400">No records found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <div>{item.usn_code}</div>
                      {item.replacement_usn && (
                        <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`replacement-${item.usn_code}`}>
                          → {item.replacement_usn}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{item.bundle_code}</td>
                    <td className="px-3 py-2 text-xs">{item.sku_code}</td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">{item.design_name || '-'}</td>
                    <td className="px-3 py-2 text-xs hidden lg:table-cell">{item.shade || '-'}</td>
                    <td className="px-3 py-2 text-xs max-w-[150px] truncate" title={item.damage_remarks}>{item.damage_remarks || '-'}</td>
                    <td className="px-3 py-2 text-xs">{item.source_section}</td>
                    <td className="px-3 py-2">
                      {item.status === 'RECEIVED' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" /> Received
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                          <AlertCircle className="h-3 w-3" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {item.result === 'CLEARED' && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" /> Cleared
                        </span>
                      )}
                      {item.result === 'DESIGN_TRANSFERRED' && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-red-100 text-red-700 px-1.5 py-0.5 rounded" title={item.design_transfer_design_name}>
                          <XCircle className="h-3 w-3" /> No Exchange
                        </span>
                      )}
                      {!item.result && item.status === 'RECEIVED' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => setDisposeRow(item)}
                          data-testid={`disposition-${item.usn_code}`}
                        >
                          <Replace className="h-3 w-3 mr-1" />
                          Disposition
                        </Button>
                      )}
                      {!item.result && item.status !== 'RECEIVED' && (
                        <span className="text-[10px] text-gray-400">Awaiting receipt</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">{item.scanned_by_name}</td>
                    <td className="px-3 py-2 text-xs hidden lg:table-cell">{item.scanned_at ? new Date(item.scanned_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between" data-testid="pagination">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <DamageDispositionDialog
        open={!!disposeRow}
        row={disposeRow}
        onClose={() => setDisposeRow(null)}
        onDone={() => fetchData()}
      />
    </div>
  );
}
